import { useEffect, useMemo, useState } from 'react';
import FeaturedSoftwareCoursesHeader from './subcomponents/FeaturedSoftwareCoursesHeader';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import UsageTracker from '../../utils/UsageTracker';
import { getRecommendedSoftwareByField, getRecommendedSoftwareByLanguage, getGenericRecommendedSoftware, SoftwareRecommendationsViewMoreListMap } from '../../features/recommendations';
import getLang from 'LearningCenterUI/utils/getLang';
import { usePreviouslySelectedTabStore } from '../../features/recommendations/recommendationsStore';
import { academyPageFullWidth, mediaQuery, useMediaQuery } from 'academy-common-lib/utils/Styling';
import { FeaturedSoftwareCoursesTabs, useSoftwareRecommendationsTabs } from './subcomponents/FeaturedSoftwareCoursesTabs';
import styled, { css } from 'foundations-theming/utils';
import { Flex } from 'foundations-components/components/Flex';
import LearningContentCard from '../../features/cards/components/LearningContentCard';
import { getSearchUrl } from '../../features/search/searchRouting';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { ACContentCardThumbnailIcon } from 'academy-common-lib/components/card/ACContentCardThumbnailIcon';
import ACInlineContentCard from 'academy-common-lib/components/card/ACInlineContentCard';
import I18n from 'I18n';
//@ts-expect-error Could not find declaration file for module
import MarketingHubLogo from '../Icons/ProductIcons/MarketingHubLogo';
//@ts-expect-error Could not find declaration file for module
import SalesHubLogo from '../Icons/ProductIcons/SalesHubLogo';
//@ts-expect-error Could not find declaration file for module
import ServiceHubLogo from '../Icons/ProductIcons/ServiceHubLogo';
//@ts-expect-error Could not find declaration file for module
import OpsHubLogo from '../Icons/ProductIcons/OpsHubLogo';
import { scrollPageToTop } from 'academy-common-lib/utils/scrolling';
import { SprocketIcon } from 'foundations-assets/icons/components/SprocketIcon';
import { HasGate } from '../../features/accounts';
import HomePageSection from '../Layouts/HomePageSection';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ObsidianSectionContent = styled.div.withConfig({
  displayName: "FeaturedSoftwareCoursesSection__ObsidianSectionContent",
  componentId: "sc-1ps5epa-0"
})(["", " padding-left:48px;padding-right:48px;padding-top:60px;padding-bottom:60px;color:", ";", ""], academyPageFullWidth, ({
  theme
}) => theme.color['text-core-default'], mediaQuery('mobile', css(["padding-top:", ";padding-bottom:", ";padding-left:", ";padding-right:", ";"], ({
  theme
}) => theme.space['600'], ({
  theme
}) => theme.space['600'], ({
  theme
}) => theme.space['400'], ({
  theme
}) => theme.space['400'])));
const HubProductLogo = {
  marketing: /*#__PURE__*/_jsx(MarketingHubLogo, {}),
  sales: /*#__PURE__*/_jsx(SalesHubLogo, {}),
  'services-support': /*#__PURE__*/_jsx(ServiceHubLogo, {}),
  'administration-operations': /*#__PURE__*/_jsx(OpsHubLogo, {}),
  other: /*#__PURE__*/_jsx(SprocketIcon, {
    color: "fill-brand-default",
    size: "800"
  })
};
export const useSoftwareRecommendationsContent = (selectedTab, maxResults = 6) => {
  const language = getLang();
  const contentToUse = useMemo(() => {
    let contentList;
    if (language === 'en') {
      contentList = getRecommendedSoftwareByField(selectedTab);
    } else {
      contentList = getRecommendedSoftwareByLanguage(language);
    }
    if (!contentList) contentList = getGenericRecommendedSoftware();
    return contentList.slice(0, maxResults);
  }, [language, maxResults, selectedTab]);
  const {
    cardModels,
    cardModelsLoading: isLoadingContent
  } = useLearningContentCardModels({
    items: contentToUse.map(({
      id,
      type
    }) => ({
      id,
      type,
      language: getLang()
    })),
    skip: !contentToUse.length
  });
  const tool = SoftwareRecommendationsViewMoreListMap[selectedTab];
  const viewAllLink = tool ? getSearchUrl({
    tool
  }) : null;
  return {
    cardModels,
    isLoadingContent,
    viewAllLink
  };
};
const FeaturedSoftwareCoursesSection = () => {
  const {
    isMobile
  } = useMediaQuery();
  const {
    isLoading: isLoadingTabs,
    defaultSelectedTab
  } = useSoftwareRecommendationsTabs();
  const maxResults = isMobile ? 5 : 6;
  const [selectedTab, setSelectedTab] = useState('other');
  const {
    cardModels,
    isLoadingContent,
    viewAllLink
  } = useSoftwareRecommendationsContent(selectedTab, maxResults);
  const {
    setPreviouslySelectedTab
  } = usePreviouslySelectedTabStore();
  const isLoading = isLoadingTabs || isLoadingContent;
  const handleSelectionChange = tab => {
    UsageTracker.track('interaction', {
      action: 'featured-software-switched-tab',
      tab
    });
    setSelectedTab(tab);
    setPreviouslySelectedTab(tab);
  };
  useEffect(() => {
    setSelectedTab(defaultSelectedTab);
  }, [defaultSelectedTab]);
  const showTabs = !isLoading && getLang() === 'en';
  const content = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FeaturedSoftwareCoursesHeader, {
      isLoadingData: isLoading,
      selectedTab: selectedTab
    }), showTabs && /*#__PURE__*/_jsx(FeaturedSoftwareCoursesTabs, {
      onSelectedChange: handleSelectionChange,
      selectedTab: selectedTab
    }), /*#__PURE__*/_jsxs(CardGrid, {
      gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, max(350px, 100%/4)), 1fr))",
      gutterSize: isMobile ? 12 : 24,
      marginBlockStart: "600",
      children: [cardModels === null || cardModels === void 0 ? void 0 : cardModels.map((item, index) => /*#__PURE__*/_jsx(LearningContentCard, {
        item: item,
        onItemClick: () => {
          UsageTracker.track('interaction', {
            action: 'featured-software-content-card-click',
            contentId: item.id,
            contentType: item.type
          });
        },
        overviewType: "featured-software",
        position: index + 1,
        size: "small",
        use: "on-surface-recessed"
      }, item.id)), viewAllLink && (cardModels === null || cardModels === void 0 ? void 0 : cardModels.length) && cardModels.length < maxResults && /*#__PURE__*/_jsx(ACInlineContentCard, {
        name: I18n.text('academyComponents.reducedContentCards.allHubCourses', {
          hub: I18n.text(`learningCenter.learningTopics.options.${selectedTab}`)
        }),
        onClick: () => {
          UsageTracker.track('interaction', {
            action: 'featured-software-view-all-clicked',
            hub: selectedTab
          });
          setTimeout(() => {
            scrollPageToTop();
          }, 100);
        },
        size: "extra-small",
        thumbnailOverlay: /*#__PURE__*/_jsx(ACContentCardThumbnailIcon, {
          backgroundColor: "fill-surface-raised",
          icon: HubProductLogo[selectedTab]
        }),
        to: viewAllLink,
        use: "on-surface-recessed"
      })]
    })]
  });
  return /*#__PURE__*/_jsx(HasGate, {
    fallback: /*#__PURE__*/_jsx(Flex, {
      backgroundColor: "fill-accent-neutral-subtle-alt",
      "data-loading": isLoading,
      justifyContent: "center",
      children: /*#__PURE__*/_jsx(ObsidianSectionContent, {
        children: content
      })
    }),
    gate: "Academy:UnifiedAcademy:Navigation",
    children: /*#__PURE__*/_jsx(HomePageSection, {
      children: content
    })
  });
};
export default FeaturedSoftwareCoursesSection;